import { Drawer, Group, Stack, TextInput, Title } from "@mantine/core"
import { useForm } from "@mantine/form"
import type { CountryIso3166 } from "@tfg/db"
import { Button, CountrySelector } from "@tfg/ui"
import { AxiosError } from "axios"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useTranslation } from "react-i18next"

import {
  prodSiteSchema,
  prodSiteValidationType,
} from "@tfg/types/schemas/productionSites"

import { useCreateProductionSiteMutation } from "~/pages/SupplierPortal/ProductionSites/hooks/useCreateProductionSiteMutation.ts"
import { COUNTRIES } from "~/utils/countries.ts"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  defaultName: string
  opened: boolean
  onClose: () => void
  onProductionSiteCreated: (productionSiteId: string) => void
}

export const ProductionSiteDrawer = ({
  defaultName,
  opened,
  onClose,
  onProductionSiteCreated,
}: Props) => {
  const { t } = useTranslation(["traceability"])

  const { mutate, isPending: isLoading } = useCreateProductionSiteMutation({
    onSuccess: (data) => {
      onProductionSiteCreated(data.id)
      handleClose()
      showSuccessNotification()
    },
    onError: (error: AxiosError) => {
      showAxiosError({
        showNotification: true,
        error: error as Error,
      })
    },
  })
  const form = useForm<prodSiteValidationType>({
    validate: zod4Resolver(prodSiteSchema),
    initialValues: {
      name: defaultName,
      address: "",
      countryIso3166: "" as CountryIso3166,
    },
  })
  const onSubmit = (values: prodSiteValidationType) => {
    mutate({
      request: values,
    })
  }
  const handleClose = () => {
    form.reset()
    onClose()
  }

  const isSubmittable =
    form.values.name && form.values.address && form.values.countryIso3166

  return (
    <Drawer
      position="right"
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>{t("productionSiteModal.title")}</Title>}
      size="xl"
    >
      <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
        <Stack gap="xl">
          <Stack gap="sm">
            <Title order={4}>
              {t("productionSiteModal.titles.information")}
            </Title>
            <TextInput
              {...form.getInputProps("name")}
              label={t("productionSiteModal.labels.name")}
            />
            <TextInput
              {...form.getInputProps("address")}
              label={t("productionSiteModal.labels.address")}
            />
            <CountrySelector
              label={t("productionSiteModal.labels.country")}
              searchable
              countriesMapper={COUNTRIES}
              onChange={form.getInputProps("countryIso3166").onChange}
              value={form.getInputProps("countryIso3166").value}
            />
          </Stack>
          <Group justify="right">
            <Button variant="tertiary" onClick={handleClose}>
              {t("productionSiteModal.actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading} disabled={!isSubmittable}>
              {t("productionSiteModal.actions.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  )
}
